/**
 * blocks.ts — planar-face BLOCK detection (W2d-3 Task 1).
 *
 * A city BLOCK is an interior face of the street graph's planar subdivision.
 * The R1/W2d-2 planarity invariant ("no two edges cross without a shared node",
 * crossings == 0 is a standing oracle) makes the graph a valid planar embedding
 * in the region chart, so its faces are well-defined and can be traced with the
 * classic half-edge rotation walk:
 *
 *   - every undirected edge e contributes TWO directed half-edges,
 *     he 2e   = A→B  and  he 2e+1 = B→A  (twin(h) = h ^ 1);
 *   - at every node the OUTGOING half-edges are sorted by chart angle
 *     (pmath.atan2, ties by half-edge id — deterministic);
 *   - next(h) = the CCW-PREDECESSOR of twin(h) in the head node's sorted
 *     outgoing list (equivalently: the next half-edge clockwise from the
 *     reversal). This walks every face with its INTERIOR ON THE LEFT, so
 *     interior faces come out CCW (positive shoelace area) and each
 *     component's unbounded outer face comes out CW (negative area).
 *
 * Consequences the oracles pin (worldgen-blocks.test.mts):
 *   - every live half-edge belongs to EXACTLY one face;
 *   - Σ signed areas over all faces of a component == 0 (interior == −outer);
 *   - positive-area face count == the graph's CYCLE RANK, E − V + C (Euler),
 *     over edge-bearing nodes — a TREE component has one zero-area face.
 *
 * SIDE CONVENTION (the strip-fallback seam, Task 2): emitParcels' side 0
 * offsets by +perp = (−dv, du) = the LEFT of A→B, which is exactly the face
 * containing he 2e; side 1 is the face containing he 2e+1. {@link faceOnSide}
 * encodes this so the lot stage can ask "was the face on this side of this
 * edge lotted?" without re-deriving the convention.
 *
 * DEGENERACIES. Self-loop edges (A == B) cannot be angle-sorted and are
 * excluded from the trace (counted in `skippedEdges`; production graphs never
 * emit them — defensive). A dangling twig is walked out-and-back by the face
 * that surrounds it (its two half-edges share one face) — no special case.
 *
 * SHARED-CODE DISCIPLINE: pmath.atan2 only (no Math.atan2 / trig / `**`);
 * the pmath-ban gate covers this file. Pure geometry — no hashing, no sampler:
 * everything here is a deterministic function of the node/edge arrays alone.
 */
import * as pmath from "../pmath.js";

/**
 * The minimal read-only graph view the trace needs — both a frozen CityGraph
 * and a CityGraphBuilder snapshot satisfy it structurally.
 */
export interface FaceGraphView {
  nodeU: ArrayLike<number>;
  nodeV: ArrayLike<number>;
  edgeA: ArrayLike<number>;
  edgeB: ArrayLike<number>;
}

export interface TracedFace {
  /** Directed half-edge ids in cycle order (he = 2e for A→B, 2e+1 for B→A). */
  halfEdges: number[];
  /** Start node of each half-edge, same order — the face polygon's vertices. */
  nodes: number[];
  /** Shoelace 2×signed area (chart m²). > 0 ⇒ CCW interior face (a block). */
  area2: number;
}

export interface FaceTrace {
  /** Faces in first-encountered half-edge order (ascending — deterministic). */
  faces: TracedFace[];
  /** Half-edge id → index into {@link faces}; −1 only for skipped self-loops. */
  faceOf: Int32Array;
  /** Self-loop edges excluded from the trace (defensive count; expect 0). */
  skippedEdges: number;
}

/** Trace every face of the planar street graph. O(E log E). */
export function traceFaces(g: FaceGraphView): FaceTrace {
  const nE = g.edgeA.length;
  const nN = g.nodeU.length;
  const faceOf = new Int32Array(2 * nE).fill(-1);

  // Outgoing half-edge lists per node + each half-edge's slot in its list.
  const out: number[][] = new Array(nN);
  for (let i = 0; i < nN; i++) out[i] = [];
  const heTail = new Int32Array(2 * nE).fill(-1); // start node of he (−1 = dead)
  let skippedEdges = 0;
  for (let e = 0; e < nE; e++) {
    const a = g.edgeA[e];
    const b = g.edgeB[e];
    if (a === b) { skippedEdges++; continue; } // self-loop: no angular order
    heTail[2 * e] = a;
    heTail[2 * e + 1] = b;
    out[a].push(2 * e);
    out[b].push(2 * e + 1);
  }
  const angleOf = (h: number): number => {
    const tail = heTail[h];
    const head = heTail[h ^ 1];
    return pmath.atan2(g.nodeV[head] - g.nodeV[tail], g.nodeU[head] - g.nodeU[tail]);
  };
  const slotOf = new Int32Array(2 * nE).fill(-1);
  for (let i = 0; i < nN; i++) {
    const list = out[i];
    if (list.length === 0) continue;
    // Sort CCW ascending; exact-angle ties (duplicate directions) break by id.
    const ang = new Map<number, number>();
    for (const h of list) ang.set(h, angleOf(h));
    list.sort((x, y) => (ang.get(x)! - ang.get(y)!) || (x - y));
    for (let s = 0; s < list.length; s++) slotOf[list[s]] = s;
  }

  // next(h) = CCW-predecessor of twin(h) among the head node's outgoing list.
  const next = (h: number): number => {
    const twin = h ^ 1;
    const list = out[heTail[twin]];
    const s = slotOf[twin];
    return list[(s - 1 + list.length) % list.length];
  };

  const faces: TracedFace[] = [];
  const maxSteps = 2 * nE + 1; // any face cycle is bounded by the half-edge count
  for (let h0 = 0; h0 < 2 * nE; h0++) {
    if (heTail[h0] < 0 || faceOf[h0] >= 0) continue;
    const halfEdges: number[] = [];
    const nodes: number[] = [];
    let area2 = 0;
    let h = h0;
    let steps = 0;
    do {
      if (++steps > maxSteps) throw new Error("traceFaces: non-terminating face walk (corrupt half-edge structure)");
      const fi = faces.length;
      faceOf[h] = fi;
      halfEdges.push(h);
      nodes.push(heTail[h]);
      const tail = heTail[h];
      const head = heTail[h ^ 1];
      area2 += g.nodeU[tail] * g.nodeV[head] - g.nodeU[head] * g.nodeV[tail];
      h = next(h);
    } while (h !== h0);
    faces.push({ halfEdges, nodes, area2 });
  }

  return { faces, faceOf, skippedEdges };
}

/**
 * Face index adjacent to edge `e` on `side` — emitParcels' side convention
 * (side 0 = +perp = left of A→B = the face containing he 2e; side 1 = right).
 * −1 only when the edge was a skipped self-loop.
 */
export function faceOnSide(trace: FaceTrace, e: number, side: number): number {
  return trace.faceOf[2 * e + side];
}

/** The face polygon's chart vertices, [u0,v0, u1,v1, …] in cycle order (CCW for blocks). */
export function facePoly(g: FaceGraphView, f: TracedFace): Float64Array {
  const poly = new Float64Array(2 * f.nodes.length);
  for (let i = 0; i < f.nodes.length; i++) {
    poly[2 * i] = g.nodeU[f.nodes[i]];
    poly[2 * i + 1] = g.nodeV[f.nodes[i]];
  }
  return poly;
}

/** Centroid of a face polygon (plain vertex mean — the block-gate probe point). */
export function faceCentroid(g: FaceGraphView, f: TracedFace, out: [number, number]): void {
  let su = 0;
  let sv = 0;
  for (const n of f.nodes) { su += g.nodeU[n]; sv += g.nodeV[n]; }
  out[0] = su / f.nodes.length;
  out[1] = sv / f.nodes.length;
}

/** Block candidacy gates beyond raw geometry (near-seed, dryness — caller-supplied). */
export interface BlockFilter {
  /** Reject faces enclosing less than this area (m²). */
  minArea: number;
  /** Reject faces enclosing more than this area (m²) — rural voids, not blocks. */
  maxArea: number;
  /**
   * Extra gate on (centroid u, centroid v, poly) — the lot stage passes the
   * near-seed (F11) + water disciplines here. Omit to accept all.
   */
  accept?: (cu: number, cv: number, poly: Float64Array) => boolean;
}

/**
 * Indices of the traced faces that qualify as BLOCKS: interior (positive area
 * beyond float noise), ≥ 3 distinct boundary vertices, area within the filter
 * band, and passing the caller's gate. Ascending face index — deterministic.
 */
export function selectBlockFaces(g: FaceGraphView, trace: FaceTrace, filter: BlockFilter): number[] {
  const picked: number[] = [];
  const c: [number, number] = [0, 0];
  for (let i = 0; i < trace.faces.length; i++) {
    const f = trace.faces[i];
    if (f.nodes.length < 3) continue;
    const area = f.area2 * 0.5;
    if (!(area > 1e-6) || area < filter.minArea || area > filter.maxArea) continue;
    if (filter.accept !== undefined) {
      faceCentroid(g, f, c);
      if (!filter.accept(c[0], c[1], facePoly(g, f))) continue;
    }
    picked.push(i);
  }
  return picked;
}
